/**
 * Phone formatting helpers.
 *
 * The customer-app collects phones via the international `<PhoneInput>` (which
 * outputs E.164 like `+5511999998888`), but we also normalize to digits-only
 * (`11999998888`) for sessionStorage / Firestore keys and for placeOrder.
 *
 * `toE164` converts a digits-only Brazilian phone back to E.164 for Firebase
 * Auth `signInWithPhoneNumber`. Handles inputs that already have a country
 * code or `+` prefix.
 */

const DEFAULT_COUNTRY = '+55';

/**
 * Strip all non-digits.
 */
export function digitsOnly(phone) {
  return (phone || '').replace(/\D/g, '');
}

/**
 * Convert any phone-ish input to E.164 (`+5511999998888`).
 * Defaults to Brazil (`+55`) when no country code is present.
 *
 * Accepts:
 *   "11999998888"          → "+5511999998888"
 *   "5511999998888"        → "+5511999998888"
 *   "+5511999998888"       → "+5511999998888"
 *   "+55 11 99999-8888"    → "+5511999998888"
 */
export function toE164(phone, country = DEFAULT_COUNTRY) {
  if (!phone) return '';
  const trimmed = String(phone).trim();
  if (trimmed.startsWith('+')) {
    return '+' + digitsOnly(trimmed);
  }
  const d = digitsOnly(trimmed);
  if (!d) return '';
  // Heuristic: BR mobiles are 11 digits (DDD + 9-digit number). If we got
  // 12+ digits and it starts with the country prefix's digits, treat as
  // already-international.
  const countryDigits = digitsOnly(country);
  if (d.length > 11 && d.startsWith(countryDigits)) {
    return '+' + d;
  }
  return country + d;
}

/**
 * Strip the country code from an E.164 to get the local digits-only form.
 * Defaults to Brazil — strips a leading "55" if present.
 */
export function fromE164(phone, country = DEFAULT_COUNTRY) {
  if (!phone) return '';
  const d = digitsOnly(phone);
  const cc = digitsOnly(country);
  if (cc && d.startsWith(cc) && d.length > cc.length) {
    return d.slice(cc.length);
  }
  return d;
}
